import { Question, ScreeningEndStatusResponse, ScreeningStatusResponse, ScreeningType } from '@estercare/ester-shared'
import { appConfig } from '../../../../config'
import { Screening } from './types'

export const getScreeningStatus = async ({
  accessToken,
}: {
  accessToken: string
}): Promise<ScreeningStatusResponse> => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/screening/status`, {
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.message || 'Failed to fetch screening status')
  }
  return await res.json()
}

export const getNextQuestion = async ({
  accessToken,
  screeningId,
}: {
  accessToken: string
  screeningId: string
}): Promise<Question> => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/screening/${screeningId}/nextQuestion`, {
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.message || 'Failed to fetch next question')
  }
  const data = await res.json()
  return data
}

export const postStartScreening = async ({
  accessToken,
  type,
}: {
  accessToken: string
  type: ScreeningType
}): Promise<Screening> => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/screening/start`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ type }),
  })
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}))
    const errorMessage: string = errorData?.message || `Failed to start screening with type ${type}`
    throw new Error(errorMessage)
  }
  return await res.json()
}

export const getPreviousQuestion = async ({
  accessToken,
  screeningId,
}: {
  accessToken: string
  screeningId: string
}): Promise<Question> => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/screening/${screeningId}/previousQuestion`, {
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.message || 'Failed to fetch previous question')
  }

  return await res.json()
}

export const patchSubmitScreening = async ({
  accessToken,
  screeningId,
}: {
  accessToken: string
  screeningId: string
}): Promise<Screening> => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/screening/${screeningId}/submit`, {
    method: 'PATCH',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.message || `Failed to submit screening with id ${screeningId}`)
  }
  return await res.json()
}

export const transitionFollowUpToStandard = async ({
  accessToken,
  screeningId,
}: {
  accessToken: string
  screeningId: string
}): Promise<Screening> => {
  const res = await fetch(
    `${appConfig.serverRootUrl}/api/private/screening/${screeningId}/transitionFollowUpToStandard`,
    {
      method: 'PATCH',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    }
  )
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.message || `Failed to transition screening with id ${screeningId}`)
  }
  return await res.json()
}

export const postAnswerAndGetNextQuestion = async ({
  accessToken,
  screeningId,
  questionId,
  answer,
}: {
  accessToken: string
  screeningId: string
  questionId: string
  answer: string[]
}): Promise<Question> => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/screening/${screeningId}/answer`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ questionId, answer }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(
      data?.message ||
        `Failed to post answer and get next question for questionId: ${questionId}. ScreeningId: ${screeningId}`
    )
  }
  return await res.json()
}

export const getScreeningEndStatus = async ({
  accessToken,
  screeningId,
}: {
  accessToken: string
  screeningId: string
}): Promise<ScreeningEndStatusResponse> => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/screening/${screeningId}/endStatus`, {
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.message || `Failed to fetch end status for screening with id ${screeningId}`)
  }
  return await res.json()
}

export const deleteScreening = async ({ accessToken }: { accessToken: string }): Promise<void> => {
  const res = await fetch(`${appConfig.serverRootUrl}/api/private/screening/`, {
    method: 'DELETE',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.message || 'Failed to delete screening')
  }
}
